import { useCallback, useEffect, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  documentToParagraphs,
  paragraphsToDocument,
  type AuthoredBlock,
  type AuthoredLesson,
  type CompletionCondition,
  type CompletionPolicy,
  type UploadedAsset,
} from '../../api.ts';
import { optionalNumber } from '../../lib/numbers.ts';
import { FileUpload } from '../../components/FileUpload.tsx';
import { SaveRow } from '../../components/SaveRow.tsx';

const BLOCK_LABELS: Record<string, string> = {
  text: 'Текст',
  video: 'Видео',
  file: 'Файл',
  image: 'Изображение',
  assignment: 'Домашнее задание',
  quiz: 'Тест',
  code: 'Код',
  embed: 'Встраивание',
};

/** What a teacher may add here. Code and embed exist in the schema but have no editor yet. */
const ADDABLE = ['text', 'video', 'file', 'assignment', 'quiz'] as const;

function stringConfig(block: AuthoredBlock, key: string): string {
  const value = block.config[key];
  return typeof value === 'string' ? value : '';
}

/**
 * What this block actually holds, said in the header.
 *
 * A teacher building a lesson has no way to tell a finished block from an empty one without
 * opening every card: a file block whose file is attached and one whose file is not look exactly
 * alike from outside. The block that matters most is the easiest to forget — the file is uploaded
 * in one click and saved in another, and missing the second leaves a lesson that looks complete
 * and teaches nothing.
 */
function blockStatus(block: AuthoredBlock): { ready: boolean; text: string } {
  switch (block.type) {
    case 'text': {
      const body = stringConfig(block, 'markdown') || stringConfig(block, 'text');
      return body.trim()
        ? { ready: true, text: 'текст написан' }
        : { ready: false, text: 'текст пустой' };
    }

    case 'video': {
      const id = stringConfig(block, 'video_id') || stringConfig(block, 'url');
      return id
        ? { ready: true, text: stringConfig(block, 'title') || 'видео добавлено' }
        : { ready: false, text: 'видео не выбрано' };
    }

    case 'file':
    case 'image': {
      const asset = stringConfig(block, 'asset_id');
      return asset
        ? { ready: true, text: `📎 ${stringConfig(block, 'title') || 'файл прикреплён'}` }
        : { ready: false, text: 'файл не прикреплён' };
    }

    case 'assignment':
      return block.ref_id
        ? { ready: true, text: block.ref_title || 'задание создано' }
        : { ready: false, text: 'задание не создано' };

    case 'quiz':
      return block.ref_id
        ? { ready: true, text: block.ref_title || 'тест создан' }
        : { ready: false, text: 'тест не создан' };

    default:
      return { ready: true, text: '' };
  }
}

/* ---- per block editors ------------------------------------------------- */

interface BlockEditorProps {
  readonly block: AuthoredBlock;
  readonly onSave: (config: Record<string, unknown>) => void;
  readonly busy: boolean;
}

function TextBlockEditor({ block, onSave, busy }: BlockEditorProps): JSX.Element {
  const [text, setText] = useState(() => stringConfig(block, 'text'));
  const dirty = text !== stringConfig(block, 'text');

  return (
    <div className="stack">
      <textarea
        className="input"
        rows={5}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Можно **выделять**, писать `код` и ставить ``` для блока кода"
      />
      <div>
        <button
          type="button"
          className="btn btn--small"
          disabled={busy || !dirty}
          onClick={() => onSave({ ...block.config, text })}
        >
          {dirty ? 'Сохранить текст' : 'Сохранено'}
        </button>
      </div>
    </div>
  );
}

/**
 * Video is not uploaded, only linked.
 *
 * The file lives on the unlisted YouTube channel and plays inside our page; the server parses the
 * link and stores the provider and id. Parsing stays on the server so the privacy parameters
 * cannot be forgotten here.
 */
function VideoBlockEditor({ block, onSave, busy }: BlockEditorProps): JSX.Element {
  const [provider, setProvider] = useState(() => stringConfig(block, 'provider') || 'youtube');
  const [link, setLink] = useState(() => stringConfig(block, 'video_id'));
  const [title, setTitle] = useState(() => stringConfig(block, 'title'));

  return (
    <div className="stack">
      <div className="formrow">
        <div className="field" style={{ maxWidth: 160 }}>
          <label>Где лежит</label>
          <select className="input" value={provider} onChange={(e) => setProvider(e.target.value)}>
            <option value="youtube">YouTube</option>
            <option value="vimeo">Vimeo</option>
          </select>
        </div>
        <div className="field" style={{ flex: 3 }}>
          <label>Ссылка или идентификатор</label>
          <input
            className="input"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </div>
      </div>
      <div className="field">
        <label>Подпись</label>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      {/* Confirmation that the server understood the link, so a typo does not reach a class. */}
      {stringConfig(block, 'video_id') ? (
        <p className="muted" style={{ fontSize: '0.84rem' }}>
          Распознано: {stringConfig(block, 'provider')} · {stringConfig(block, 'video_id')}
        </p>
      ) : null}
      <div>
        <button
          type="button"
          className="btn btn--small"
          disabled={busy || link.trim().length === 0}
          onClick={() => onSave({ ...block.config, provider, video_id: link.trim(), title })}
        >
          Сохранить видео
        </button>
      </div>
    </div>
  );
}

/**
 * Asked before a video block exists.
 *
 * The server refuses a video block whose config names no provider and no id, and it is right to:
 * an empty video block is a hole in a published lesson. So the link is collected first and the
 * block is created already valid.
 */
function VideoComposer({
  busy,
  onCreate,
  onCancel,
}: {
  busy: boolean;
  onCreate: (config: Record<string, unknown>) => void;
  onCancel: () => void;
}): JSX.Element {
  const [provider, setProvider] = useState('youtube');
  const [link, setLink] = useState('');
  const [title, setTitle] = useState('');

  return (
    <section className="editcard stack">
      <strong>Новое видео</strong>
      <div className="formrow">
        <div className="field" style={{ maxWidth: 160 }}>
          <label htmlFor="v-provider">Где лежит</label>
          <select
            id="v-provider"
            className="input"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          >
            <option value="youtube">YouTube</option>
            <option value="vimeo">Vimeo</option>
          </select>
        </div>
        <div className="field" style={{ flex: 3 }}>
          <label htmlFor="v-link">Ссылка или идентификатор</label>
          <input
            id="v-link"
            className="input"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor="v-title">Подпись</label>
        <input
          id="v-title"
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="row">
        <button
          type="button"
          className="btn btn--small"
          disabled={busy || link.trim().length === 0}
          onClick={() => onCreate({ provider, video_id: link.trim(), title })}
        >
          Добавить видео
        </button>
        <button type="button" className="btn btn--ghost btn--small" onClick={onCancel}>
          Отмена
        </button>
      </div>
    </section>
  );
}

function FileBlockEditor({
  block,
  onSave,
  busy,
  lessonId,
}: BlockEditorProps & { lessonId: string }): JSX.Element {
  const [files, setFiles] = useState<UploadedAsset[]>([]);
  const [title, setTitle] = useState(() => stringConfig(block, 'title'));
  const current = stringConfig(block, 'asset_id');

  return (
    <div className="stack">
      {current ? (
        <p className="muted">
          Сейчас прикреплён:{' '}
          {/* Opens rather than downloads, so checking the right file is attached costs one click. */}
          <a href={api.assetViewUrl(current)} target="_blank" rel="noreferrer">
            {title || 'файл'}
          </a>
        </p>
      ) : null}

      {/*
       * Whether the assistant could read it, said where the file is attached.
       *
       * A deck exported as pictures looks perfect on screen and carries no text at all, so the
       * assistant answers "there is nothing about this in the lesson" while the teacher is looking
       * straight at the slide that covers it. Nothing in the interface used to reveal that, and
       * the only symptom was an assistant that seemed broken.
       */}
      {current && typeof block.assistant_chars === 'number' ? (
        block.assistant_chars > 0 ? (
          <p className="muted" style={{ fontSize: '0.84rem' }}>
            Помощник прочитал файл: {block.assistant_chars.toLocaleString('ru')} знаков.
          </p>
        ) : (
          <div className="notice notice--accent" style={{ fontSize: '0.86rem' }}>
            <strong>Помощник не смог прочитать этот файл.</strong> Скорее всего слайды сохранены
            картинками — текста в файле нет. Ученики презентацию увидят, но на вопросы по ней
            помощник отвечать не сможет. Пересохраните из PowerPoint как PDF, не «экспорт в
            изображения».
          </div>
        )
      ) : null}

      {/*
       * Said before the upload, not after: a teacher who has already attached a .pptx and seen it
       * download has been let down by a screen that knew in advance.
       */}
      <p className="muted" style={{ fontSize: '0.84rem' }}>
        PDF и картинки открываются прямо в уроке. Презентацию сохраните из PowerPoint как PDF —
        файл .pptx браузер показать не умеет, ученик сможет только скачать его.
      </p>

      <div className="field">
        <label>Подпись</label>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>

      <FileUpload
        maxFiles={1}
        maxFileMb={100}
        files={files}
        onChange={setFiles}
        disabled={busy}
        label="Выбрать файл"
      />

      <div>
        <button
          type="button"
          className="btn btn--small"
          disabled={busy || (files.length === 0 && !current)}
          onClick={() => {
            const asset = files[0];
            const assetId = asset?.id ?? current;
            onSave({ ...block.config, asset_id: assetId, title: title || asset?.original_name });
            // The lesson has to claim the file, or a learner who does not own it cannot open it.
            if (asset) {
              void api.authoring
                .attach({ asset_id: asset.id, target_type: 'lesson', target_id: lessonId })
                .catch(() => undefined);
            }
          }}
        >
          Сохранить файл
        </button>
      </div>
    </div>
  );
}

/**
 * Builds the completion rule out of the blocks the lesson actually has.
 *
 * Offering the four condition types as free-form fields would let a teacher require a quiz the
 * lesson does not contain, and the learner would then face a condition nothing could satisfy.
 * So the choices are derived from the blocks that exist.
 */
function CompletionEditor({
  lesson,
  busy,
  onSave,
}: {
  lesson: AuthoredLesson;
  busy: boolean;
  onSave: (policy: CompletionPolicy) => void;
}): JSX.Element {
  // Annotated once, so each entry below is checked against the union rather than widened to string.
  const available: { key: string; label: string; condition: CompletionCondition }[] = [
    { key: 'view', label: 'Открыть урок', condition: { type: 'view' } },
    ...lesson.blocks
      .filter((b) => b.type === 'video')
      .map((b) => ({
        key: `video:${b.id}`,
        label: 'Досмотреть видео на 80%',
        condition: { type: 'video' as const, block_id: b.id, threshold_percent: 80 },
      })),
    ...lesson.blocks
      .filter((b) => b.type === 'file' || b.type === 'image')
      .map((b) => ({
        key: `material:${b.id}`,
        label: `Посмотреть «${typeof b.config.title === 'string' && b.config.title ? b.config.title : 'материал'}»`,
        condition: { type: 'material' as const, block_id: b.id },
      })),
    ...lesson.blocks
      .filter((b) => b.type === 'assignment' && b.ref_id)
      .map((b) => ({
        key: `assignment:${b.ref_id ?? ''}`,
        label: 'Отправить домашнее задание (в рейтинг — после принятия)',
        condition: {
          type: 'assignment' as const,
          assignment_id: b.ref_id ?? '',
          /*
           * Accepted, not merely handed in.
           *
           * This wrote 'submitted' every time the rule was saved, so a lesson ticked itself off
           * the moment a file was uploaded — and stayed ticked when the teacher read the work and
           * sent it back. The engine's default was changed to 'graded', but a value written here
           * explicitly overrides a default, so the two disagreed and the explicit one won.
           */
          min_state: 'graded' as const,
        },
      })),
    ...lesson.blocks
      .filter((b) => b.type === 'quiz' && b.ref_id)
      .map((b) => ({
        key: `quiz:${b.ref_id ?? ''}`,
        label: 'Пройти тест на 60%',
        condition: {
          type: 'quiz' as const,
          quiz_id: b.ref_id ?? '',
          min_fraction: 0.6,
        },
      })),
  ];

  const keyOf = (condition: CompletionCondition): string =>
    condition.type === 'view'
      ? 'view'
      : `${condition.type}:${condition.block_id ?? condition.assignment_id ?? condition.quiz_id ?? ''}`;

  const active = new Set(lesson.completion_policy.conditions.map(keyOf));

  return (
    <div className="stack" style={{ marginTop: 12 }}>
      {available.map((option) => (
        <label key={option.key} className="checkline">
          <input
            type="checkbox"
            checked={active.has(option.key)}
            disabled={busy}
            onChange={(e) => {
              /*
               * Rebuilt from the list as it is now, and always without this key first.
               *
               * Ticking two boxes quickly used to lose the first: each change was computed from
               * the conditions this row had rendered with, so the second save was written from a
               * list that predated the first. A checklist that silently forgets what was just
               * ticked is worse than one that cannot be edited at all.
               */
              const without = lesson.completion_policy.conditions.filter(
                (c) => keyOf(c) !== option.key,
              );

              onSave({
                require: lesson.completion_policy.require,
                conditions: e.target.checked ? [...without, option.condition] : without,
              });
            }}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

/* ---- the screen -------------------------------------------------------- */

export function LessonEditorPage(): JSX.Element {
  const { lessonId } = useParams();
  const [lesson, setLesson] = useState<AuthoredLesson | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [minutes, setMinutes] = useState('');
  const [prose, setProse] = useState('');
  const [addingVideo, setAddingVideo] = useState(false);

  const load = useCallback((): void => {
    if (!lessonId) return;
    api.authoring
      .lesson(lessonId)
      .then((loaded) => {
        setLesson(loaded);
        setTitle(loaded.title);
        setMinutes(loaded.estimated_minutes === null ? '' : String(loaded.estimated_minutes));
        setProse(documentToParagraphs(loaded.content).join('\n\n'));
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить урок'),
      );
  }, [lessonId]);

  useEffect(load, [load]);

  async function run(action: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError('');
    try {
      await action();
      load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  }

  if (error && !lesson) {
    return (
      <div className="stack" style={{ paddingTop: 20 }}>
        <div className="notice notice--accent">{error}</div>
        <Link to="/admin" className="back">
          ← Курсы
        </Link>
      </div>
    );
  }

  if (!lesson || !lessonId) return <p className="muted">Загружаем…</p>;

  const current = lesson;
  const id = lessonId;

  function addBlock(type: string): void {
    // Video is the one kind that cannot be created empty, so it collects its link first.
    if (type === 'video') {
      setAddingVideo(true);
      return;
    }

    void run(async () => {
      // A homework or quiz block is a pointer, so the thing it points at has to exist first.
      if (type === 'assignment') {
        const created = await api.authoring.createAssignment(id, {
          title: `Домашнее задание: ${current.title}`,
          allow_text: true,
          allow_files: true,
          max_files: 3,
          max_file_mb: 10,
        });
        await api.authoring.createBlock(id, { type, ref_id: created.id });
        return;
      }

      if (type === 'quiz') {
        const created = await api.authoring.createQuiz(id, { title: `Тест: ${current.title}` });
        await api.authoring.createBlock(id, { type, ref_id: created.id });
        return;
      }

      await api.authoring.createBlock(id, { type, config: {} });
    });
  }

  function moveBlock(index: number, delta: number): void {
    const target = index + delta;
    if (target < 0 || target >= current.blocks.length) return;

    const order = current.blocks.map((b) => b.id);
    const [moved] = order.splice(index, 1);
    if (moved === undefined) return;
    order.splice(target, 0, moved);
    void run(() => api.authoring.reorder('block', id, order));
  }

  const savedMinutes = current.estimated_minutes === null ? '' : String(current.estimated_minutes);
  const savedProse = documentToParagraphs(current.content).join('\n\n');
  const dirty = title !== current.title || minutes !== savedMinutes || prose !== savedProse;

  return (
    <>
      <div className="page-head">
        <Link to={`/admin/courses/${current.course_id}`} className="back">
          ← {current.course_title}
        </Link>
        <h1>{current.title}</h1>
        <p className="muted">Модуль: {current.module_title}</p>
      </div>

      {error ? <p className="error">{error}</p> : null}

      <section className="editcard stack">
        <strong>Урок</strong>
        <div className="formrow">
          <div className="field" style={{ flex: 3 }}>
            <label htmlFor="lesson-title">Название</label>
            <input
              id="lesson-title"
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="lesson-min">Минут</label>
            <input
              id="lesson-min"
              className="input"
              type="number"
              min={1}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="lesson-prose">Текст урока</label>
          <textarea
            id="lesson-prose"
            className="input"
            rows={6}
            value={prose}
            onChange={(e) => setProse(e.target.value)}
            placeholder="Абзацы разделяются пустой строкой"
          />
        </div>

        {/*
         * A real confirmation instead of a button that renames itself. "Сохранено" as a label
         * says "there is nothing to save", which is also what it says before anything was ever
         * typed — so it could never tell a teacher that their work had actually landed.
         */}
        <SaveRow
          label="Сохранить урок"
          disabled={busy || !dirty}
          onSave={async () => {
            await api.authoring.updateLesson(id, {
              ...(title !== current.title ? { title: title.trim() } : {}),
              ...(minutes !== savedMinutes
                ? { estimated_minutes: optionalNumber(minutes) }
                : {}),
              ...(prose !== savedProse ? { content: paragraphsToDocument(prose) } : {}),
            });
            load();
          }}
        />
      </section>

      <h2 style={{ margin: '28px 0 14px' }}>Блоки урока</h2>

      {/*
       * The lesson at a glance, before the cards.
       *
       * The question a teacher actually has at this point is "did I forget anything" — and the
       * honest answer is a list of what is missing, not a wall of collapsed blocks that all look
       * the same whether they are finished or not.
       */}
      {current.blocks.length > 0 ? (
        <div className="lessonsum">
          {current.blocks.map((block) => {
            const state = blockStatus(block);
            if (!state.text) return null;

            return (
              <span
                key={`sum-${block.id}`}
                className={state.ready ? 'lessonsum__item lessonsum__item--ok' : 'lessonsum__item'}
                title={state.text}
              >
                {state.ready ? '✓' : '•'} {BLOCK_LABELS[block.type] ?? block.type}
              </span>
            );
          })}
        </div>
      ) : null}

      {current.blocks.length === 0 ? (
        <div className="notice">
          Блоков пока нет. Добавьте текст, видео, файл, домашнее задание или тест.
        </div>
      ) : null}

      {current.blocks.map((block, index) => (
        <section key={block.id} className="editcard">
          <header className="editcard__head">
            <span className="badge">{BLOCK_LABELS[block.type] ?? block.type}</span>
            <span className="rowcard__main">
              {(() => {
                const state = blockStatus(block);
                if (!state.text) return null;
                return (
                  <span className={state.ready ? 'blockstate blockstate--ok' : 'blockstate'}>
                    {state.ready ? '✓ ' : '• '}
                    {state.text}
                  </span>
                );
              })()}
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={busy || index === 0}
              onClick={() => moveBlock(index, -1)}
              aria-label="Блок выше"
            >
              ↑
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={busy || index === current.blocks.length - 1}
              onClick={() => moveBlock(index, 1)}
              aria-label="Блок ниже"
            >
              ↓
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--small btn--danger"
              disabled={busy}
              onClick={() => {
                if (window.confirm('Убрать блок из урока?')) {
                  void run(() => api.authoring.deleteBlock(block.id));
                }
              }}
            >
              Убрать
            </button>
          </header>

          {block.type === 'text' ? (
            <TextBlockEditor
              block={block}
              busy={busy}
              onSave={(config) => void run(() => api.authoring.updateBlock(block.id, { config }))}
            />
          ) : null}

          {block.type === 'video' ? (
            <VideoBlockEditor
              block={block}
              busy={busy}
              onSave={(config) => void run(() => api.authoring.updateBlock(block.id, { config }))}
            />
          ) : null}

          {block.type === 'file' || block.type === 'image' ? (
            <FileBlockEditor
              block={block}
              busy={busy}
              lessonId={id}
              onSave={(config) => void run(() => api.authoring.updateBlock(block.id, { config }))}
            />
          ) : null}

          {block.type === 'assignment' && block.ref_id ? (
            <Link className="btn btn--ghost btn--small" to={`/admin/assignments/${block.ref_id}`}>
              Настроить задание
            </Link>
          ) : null}

          {block.type === 'quiz' && block.ref_id ? (
            <Link className="btn btn--ghost btn--small" to={`/admin/quizzes/${block.ref_id}`}>
              Собрать тест
            </Link>
          ) : null}
        </section>
      ))}

      {addingVideo ? (
        <VideoComposer
          busy={busy}
          onCancel={() => setAddingVideo(false)}
          onCreate={(config) =>
            void run(async () => {
              await api.authoring.createBlock(id, { type: 'video', config });
              setAddingVideo(false);
            })
          }
        />
      ) : null}

      <div className="row" style={{ marginTop: 18 }}>
        <span className="muted">Добавить:</span>
        {ADDABLE.map((type) => (
          <button
            key={type}
            type="button"
            className="btn btn--ghost btn--small"
            disabled={busy}
            onClick={() => addBlock(type)}
          >
            + {BLOCK_LABELS[type]}
          </button>
        ))}
      </div>

      <section className="editcard" style={{ marginTop: 28 }}>
        <strong>Как засчитывается урок</strong>
        <p className="muted" style={{ fontSize: '0.88rem' }}>
          {current.completion_policy.conditions.length === 0
            ? 'Ничего не отмечено — урок засчитывается по факту открытия.'
            : `Нужно выполнить ${current.completion_policy.require === 'all' ? 'все отмеченное' : 'любое из отмеченного'}:`}
        </p>
        <CompletionEditor
          lesson={current}
          busy={busy}
          onSave={(policy) =>
            void run(() => api.authoring.updateLesson(id, { completion_policy: policy }))
          }
        />
      </section>
    </>
  );
}
